import {useQuery} from "react-query";
import {ReservationGroupService} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {RateCalculationService} from "crqlar-react-common/dist/services/rate/rateCalculation.service";
import {findRateByDate} from "crqlar-react-common/dist/utils/rate.util";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";

/*
    A "package" booking is a reservation group whose serviceId points at a PACKAGE service
    (created in AddNewPackageReservationsDialog). Restaurant serial / multiday groups are also
    reservation groups, but they carry no service — so the package service is what tells them apart.
*/
export const usePackageGroup = (reservationGroupId?: string) => {
    const reservationGroupQuery = useQuery(
        ['reservationGroup', reservationGroupId],
        () => ReservationGroupService.getReservationGroupById(reservationGroupId!),
        {enabled: !!reservationGroupId}
    )
    const groupService = useService(reservationGroupQuery.data?.serviceId)

    return {
        reservationGroup: reservationGroupQuery.data,
        packageService: groupService?.isPackage ? groupService : undefined
    }
}

// Earliest scheduled member — the package is priced on the day it starts, not on whichever member was opened.
const packageStartDate = (linkedReservations: Reservation[], fallback?: Date) => {
    const starts = linkedReservations
        .map(r => r.expectedStartDate)
        .filter((d): d is Date => !!d)
        .map(d => new Date(d))
    if (!starts.length) return fallback || new Date()
    return new Date(Math.min(...starts.map(d => d.getTime())))
}

const calculateRate = async (service: Service | undefined, onDate: Date) => {
    if (!service?.rateIds?.length) return undefined
    const rates = await RateCalculationService.calculate({ids: service.rateIds, onDate})
    return findRateByDate(rates, onDate)
}

interface PreparePackageFolioParams {
    reservationGroup: ReservationGroup
    packageService: Service
    profileId?: string
    currencyCode?: string
}

// Loaded here rather than taken from the dialog: not every reservation dialog (e.g. the spa one) has the
// group members at hand, and a folio built from a partial list silently drops members and extras.
const loadPackageMembers = async (reservationGroupId: string) => {
    const linkedReservations = (await ReservationService.getAllReservations({
        reservationGroupId: [reservationGroupId],
        limit: {offset: 0, limit: 100}
    })).content || []

    const serviceIds = Array.from(new Set(linkedReservations.map(r => r.serviceId).filter((id): id is string => !!id)))
    const services = serviceIds.length
        ? (await ServiceService.getAllServices({id: serviceIds, limit: {offset: 0, limit: 100}})).content || []
        : []

    return {linkedReservations, services}
}

const priceFor = (rate: Awaited<ReturnType<typeof calculateRate>>, currencyCode?: string) => {
    const amount = rate?.price?.amount
    return {
        ...rate?.price,
        // The backend derives the item total from amountCents, so never send a price with only `amount`.
        amountCents: rate?.price?.amountCents ?? (amount != null ? Math.round(amount * 100) : undefined),
        currency: rate?.price?.currency || currencyCode || "EUR",
    }
}

const buildMemberItem = async (
    folioId: string,
    reservation: Reservation,
    service: Service | undefined,
    fallbackDate: Date,
    currencyCode?: string
): Promise<FolioItem> => {
    const onDate = reservation.expectedStartDate ? new Date(reservation.expectedStartDate) : fallbackDate
    const rate = await calculateRate(service, onDate)
    return {
        folioId,
        timeSpanStart: reservation.expectedStartDate || fallbackDate,
        timeSpanEnd: reservation.expectedEndDate,
        description: service?.name || "",
        serviceInventoryCode: service?.marketCode || "",
        // Attribute the line to its service so downstream PMS charges resolve the right
        // commodity group / VAT instead of falling back to the connector default.
        serviceId: service?.id,
        price: priceFor(rate, currencyCode),
        taxRateId: rate?.taxRateId,
        taxRateSnap: rate?.taxRateSnap,
        inclusive: false
    }
}

/*
    Keeps an existing DRAFT package folio in line with its reservation group every time it is opened:
      - drops the old priced "package" line (folios built before 2026-09-29 had one)
      - prices every member row that has no price yet (old inclusive rows, rows built before a rate existed)
      - adds a row for every member booked AFTER the folio was created (e.g. an extra added later) —
        members that existed at creation but have no row were removed by staff and stay removed
    Rows staff added by hand keep their price.
*/
const syncPackageFolio = async (
    folio: Folio,
    reservationGroupId: string,
    packageService: Service,
    currencyCode?: string
) => {
    const folioId = folio.id!
    const items = (await FolioItemService.getAllFolioItems({
        folioIds: [folioId],
        limit: {offset: 0, limit: 100, sort: [{field: 'id', direction: 'ASC'}]}
    })).content || []

    const {linkedReservations, services} = await loadPackageMembers(reservationGroupId)
    const fallbackDate = packageStartDate(linkedReservations)
    const usedReservationIds = new Set<string>()

    const packageLines = items.filter(i => !!i.id && !!packageService.id && i.serviceId === packageService.id)
    const memberLines = items.filter(i => !!i.id && !i.archived && !packageLines.includes(i))

    for (const line of memberLines) {
        const service = services.find(s => s.id === line.serviceId) || services.find(s => s.name === line.description)
        // Pair each row with an unused member of the same service (its date prices the row).
        const reservation = linkedReservations.find(r => !!service?.id && r.serviceId === service.id
            && !!r.id && !usedReservationIds.has(r.id))
        if (reservation?.id) usedReservationIds.add(reservation.id)

        if (!line.inclusive && line.price?.amount) continue
        if (!service) continue

        const built = await buildMemberItem(folioId, reservation || {} as Reservation, service, fallbackDate, currencyCode)
        if (!built.price?.amount && !line.inclusive) continue
        await FolioItemService.updateFolioItem({
            ...line,
            description: line.description || built.description,
            serviceInventoryCode: line.serviceInventoryCode || built.serviceInventoryCode,
            serviceId: line.serviceId || built.serviceId,
            timeSpanStart: line.timeSpanStart || built.timeSpanStart,
            timeSpanEnd: line.timeSpanEnd || built.timeSpanEnd,
            price: built.price,
            taxRateId: line.taxRateId || built.taxRateId,
            taxRateSnap: line.taxRateSnap || built.taxRateSnap,
            inclusive: false
        })
    }

    const folioCreatedAt = folio.insertDate ? new Date(folio.insertDate).getTime() : undefined
    const bookedLater = linkedReservations.filter(r => !!r.id && !usedReservationIds.has(r.id)
        && folioCreatedAt != null && !!r.insertDate && new Date(r.insertDate).getTime() > folioCreatedAt)
    for (const reservation of bookedLater) {
        const service = services.find(s => s.id === reservation.serviceId)
        await FolioItemService.createFolioItem(
            await buildMemberItem(folioId, reservation, service, fallbackDate, currencyCode)
        )
    }

    const packageLineIds = packageLines.map(i => i.id!).filter(Boolean)
    if (packageLineIds.length) await FolioItemService.deleteFolioItem({id: packageLineIds})
}

/**
 * Returns the id of the package's folio, creating it on first use.
 *
 * The folio is attached to the reservation GROUP (not to the member reservation that was opened), so
 * the whole package is charged at once. Every service of the package is its own priced row (at that
 * service's rate on its own date), extras added on top are further priced rows, and the folio total is
 * the sum of all rows. There is no separate "package" line.
 */
export const preparePackageFolio = async ({
                                              reservationGroup,
                                              packageService,
                                              profileId,
                                              currencyCode
                                          }: PreparePackageFolioParams): Promise<string | undefined> => {
    const existingFolio = (await FolioService.getByReservationGroupId(reservationGroup.id!))?.[0]
    if (existingFolio) {
        if (existingFolio.id && existingFolio.status === FolioStatus.DRAFT) {
            await syncPackageFolio(existingFolio, reservationGroup.id!, packageService, currencyCode)
        }
        return existingFolio.id
    }

    const {linkedReservations, services} = await loadPackageMembers(reservationGroup.id!)
    const startDate = packageStartDate(linkedReservations)

    const createdFolio = await FolioService.create({
        name: packageService.name || "Package",
        status: FolioStatus.DRAFT,
        profileId: profileId || reservationGroup.primaryProfileId || reservationGroup.profileIds?.[0],
        reservationGroupId: reservationGroup.id
    })
    if (!createdFolio?.id) return undefined

    // Package services first (in booking order), extras after them.
    const ordered = [
        ...linkedReservations.filter(r => r.reservationFromExtraPackageService !== true),
        ...linkedReservations.filter(r => r.reservationFromExtraPackageService === true)
    ]
    for (const linkedReservation of ordered) {
        const memberService = services.find(s => s.id === linkedReservation.serviceId)
        await FolioItemService.createFolioItem(
            await buildMemberItem(createdFolio.id, linkedReservation, memberService, startDate, currencyCode)
        )
    }

    return createdFolio.id
}
